import React, { useState } from 'react';
import { Settings, Zap, TrendingUp, Clock, Target, BarChart3, AlertCircle } from 'lucide-react';

const HyperparameterOptimizer = ({ 
  modelType, 
  onOptimizationToggle, 
  onConfigChange,
  isEnabled = false,
  className = ""
}) => {
  const [config, setConfig] = useState({
    method: 'random',
    cv_folds: 5,
    n_iterations: 50,
    scoring: 'accuracy'
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    onConfigChange(newConfig);
  };

  const optimizationMethods = [
    {
      value: 'random',
      label: 'Random Search',
      description: 'Randomly samples hyperparameters. Fast and often effective.',
      icon: '🎲',
      time: 'Fast',
      recommended: true
    },
    {
      value: 'grid',
      label: 'Grid Search',
      description: 'Tests all combinations systematically. Thorough but slow.',
      icon: '📊',
      time: 'Slow',
      recommended: false
    },
    {
      value: 'bayesian',
      label: 'Bayesian Optimization',
      description: 'Smart search using previous results. Most efficient for complex spaces.',
      icon: '🧠',
      time: 'Medium',
      recommended: false
    }
  ];

  const scoringOptions = {
    classification: [
      { value: 'accuracy', label: 'Accuracy' },
      { value: 'f1', label: 'F1 Score' },
      { value: 'precision', label: 'Precision' },
      { value: 'recall', label: 'Recall' },
      { value: 'roc_auc', label: 'ROC AUC' }
    ],
    regression: [
      { value: 'r2', label: 'R² Score' },
      { value: 'neg_mean_squared_error', label: 'Negative MSE' },
      { value: 'neg_mean_absolute_error', label: 'Negative MAE' },
      { value: 'neg_root_mean_squared_error', label: 'Negative RMSE' }
    ]
  };

  const isClassification = ['random_forest', 'decision_tree', 'svc', 'logistic_regression', 'gradient_boosting'].includes(modelType);
  const availableScoring = isClassification ? scoringOptions.classification : scoringOptions.regression;

  const getEstimatedTime = () => {
    const baseTime = {
      random: config.n_iterations * 0.5,
      grid: config.n_iterations * 1.5,
      bayesian: config.n_iterations * 0.8
    };
    
    const time = baseTime[config.method] * config.cv_folds / 60; // Convert to minutes
    
    if (time < 1) return '< 1 minute';
    if (time < 60) return `~${Math.round(time)} minutes`;
    return `~${Math.round(time / 60)} hours`;
  };

  return (
    <div className={`bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Settings className="w-5 h-5 text-purple-600" />
          <span className="font-semibold text-purple-800">Hyperparameter Optimization</span>
          <span className="px-2 py-1 bg-purple-500 text-white text-xs rounded-full">ADVANCED</span>
        </div>
        
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => onOptimizationToggle(e.target.checked)}
              className="w-4 h-4 text-purple-600 border-purple-300 rounded focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-purple-700">
              Enable Optimization
            </span>
          </label>
        </div>
      </div>

      {/* Description */}
      <div className="mb-4">
        <p className="text-purple-700 text-sm">
          Automatically find the best hyperparameters for your model. This can significantly improve performance 
          but will take longer to train.
        </p>
      </div>

      {/* Configuration Panel */}
      {isEnabled && (
        <div className="space-y-4 bg-white rounded-lg p-4 border border-purple-200">
          
          {/* Optimization Method Selection */}
          <div>
            <label className="block font-medium text-gray-700 mb-2">
              Optimization Method
            </label>
            <div className="grid grid-cols-1 gap-3">
              {optimizationMethods.map((method) => (
                <div
                  key={method.value}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    config.method === method.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-purple-25'
                  }`}
                  onClick={() => handleConfigChange('method', method.value)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        checked={config.method === method.value}
                        onChange={() => handleConfigChange('method', method.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-lg">{method.icon}</span>
                          <span className="font-medium text-gray-800">{method.label}</span>
                          {method.recommended && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{method.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-purple-600">
                        {method.time}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Basic Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-medium text-gray-700 mb-1">
                Cross-Validation Folds
              </label>
              <select
                value={config.cv_folds}
                onChange={(e) => handleConfigChange('cv_folds', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black"
              >
                <option value={3}>3-Fold (Fast)</option>
                <option value={5}>5-Fold (Recommended)</option>
                <option value={10}>10-Fold (Thorough)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">More folds = more reliable but slower</p>
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-1">
                {config.method === 'grid' ? 'Max Combinations' : 'Iterations'}
              </label>
              <select
                value={config.n_iterations}
                onChange={(e) => handleConfigChange('n_iterations', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black"
              >
                <option value={20}>20 (Quick)</option>
                <option value={50}>50 (Balanced)</option>
                <option value={100}>100 (Thorough)</option>
                <option value={200}>200 (Extensive)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">More iterations = better results but slower</p>
            </div>
          </div>

          {/* Scoring Metric */}
          <div>
            <label className="block font-medium text-gray-700 mb-1">
              Optimization Metric
            </label>
            <select
              value={config.scoring}
              onChange={(e) => handleConfigChange('scoring', e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-black"
            >
              {availableScoring.map((score) => (
                <option key={score.value} value={score.value}>
                  {score.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">The metric to optimize during hyperparameter search</p>
          </div>

          {/* Advanced Settings Toggle */}
          <div className="border-t pt-3">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center space-x-2 text-purple-600 hover:text-purple-800 text-sm font-medium"
            >
              <Settings className="w-4 h-4" />
              <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Settings</span>
            </button>
          </div>

          {/* Advanced Settings */}
          {showAdvanced && (
            <div className="space-y-3 bg-gray-50 p-3 rounded border-t">
              <h4 className="font-medium text-gray-800">Advanced Configuration</h4>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Random State
                  </label>
                  <input
                    type="number"
                    value={config.random_state || 42}
                    onChange={(e) => handleConfigChange('random_state', parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parallel Jobs
                  </label>
                  <select
                    value={config.n_jobs || -1}
                    onChange={(e) => handleConfigChange('n_jobs', parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value={1}>1 (Sequential)</option>
                    <option value={2}>2 Cores</option>
                    <option value={4}>4 Cores</option>
                    <option value={-1}>All Cores</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Estimated Time and Performance */}
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-800">Estimated Time</span>
              </div>
              <span className="text-purple-700 font-bold">{getEstimatedTime()}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-800">Expected Improvement</span>
              </div>
              <span className="text-purple-700 font-bold">5-15%</span>
            </div>
            
            <div className="mt-2 text-xs text-purple-600">
              * Times are estimates and may vary based on dataset size and system performance
            </div>
          </div>

          {/* Warning for long operations */}
          {(config.method === 'grid' && config.n_iterations > 100) || 
           (config.method !== 'grid' && config.n_iterations > 150) && (
            <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
              <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
              <div className="text-sm">
                <div className="font-medium text-yellow-800">Long Operation Warning</div>
                <div className="text-yellow-700">
                  This configuration may take a very long time. Consider reducing iterations or using Random Search.
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Benefits Info */}
      {!isEnabled && (
        <div className="bg-white rounded-lg p-3 border border-purple-200">
          <h4 className="font-medium text-purple-800 mb-2">Benefits of Hyperparameter Optimization:</h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li className="flex items-center space-x-2">
              <Zap className="w-3 h-3" />
              <span>Automatically finds the best model settings</span>
            </li>
            <li className="flex items-center space-x-2">
              <Target className="w-3 h-3" />
              <span>Often improves model accuracy by 5-15%</span>
            </li>
            <li className="flex items-center space-x-2">
              <BarChart3 className="w-3 h-3" />
              <span>Saves time compared to manual tuning</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default HyperparameterOptimizer;